import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getPingMonitor, CONNECTION_STATUS, SIGNAL_STRENGTH, SPEED_LEVEL } from './pingMonitor';
import WifiIcon, { SimpleWifiIcon, GradientWifiIcon } from './WifiIcon';

/**
 * WiFi Signal Component ที่เชื่อมต่อกับ Ping Monitor
 * แสดงสถานะการเชื่อมต่ออินเทอร์เน็ตแบบ real-time
 */
const WifiSignal = ({
  type = 'simple', // 'simple', 'curved', 'gradient'
  size = 18,
  showTooltip = true,
  activeColor = '#0FEF9E',
  inactiveColor = 'rgba(255, 255, 255, 0.2)',
  onStatusChange = null
}) => {
  const [connectionState, setConnectionState] = useState({
    isOnline: navigator.onLine,
    latency: null,
    strength: SIGNAL_STRENGTH.EXCELLENT,
    downloadSpeed: null,
    speedLevel: null,
    connectionStatus: CONNECTION_STATUS.CHECKING
  });

  const [showTooltipState, setShowTooltipState] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: '50%', transform: 'translateX(-50%)' });
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  const handleStatusChange = useCallback(
    (status) => {
      setConnectionState(status);
      if (onStatusChange) {
        onStatusChange(status);
      }
    },
    [onStatusChange]
  );

  useEffect(() => {
    const pingMonitor = getPingMonitor();
    const unsubscribe = pingMonitor.subscribe(handleStatusChange);
    pingMonitor.startMonitoring();

    const initialStatus = pingMonitor.getStatus();
    setConnectionState(initialStatus);

    return () => {
      unsubscribe();
    };
  }, [handleStatusChange]);

  // ตรวจสอบและปรับตำแหน่ง tooltip เมื่อแสดง
  useEffect(() => {
    if (showTooltipState && tooltipRef.current && containerRef.current) {
      const tooltip = tooltipRef.current;
      const container = containerRef.current;
      const tooltipRect = tooltip.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      const viewportWidth = window.innerWidth;
      const margin = 10; // ระยะห่างจากขอบจอ
      
      let newPosition = { left: '50%', transform: 'translateX(-50%)' };
      
      // ตรวจสอบว่าล้นขวา
      if (tooltipRect.right > viewportWidth - margin) {
        newPosition = { 
          right: '0',
          left: 'auto',
          transform: 'none'
        };
      }
      // ตรวจสอบว่าล้นซ้าย
      else if (tooltipRect.left < margin) {
        newPosition = { 
          left: '0',
          right: 'auto',
          transform: 'none'
        };
      }
      
      setTooltipPosition(newPosition);
    }
  }, [showTooltipState]);

  const getTooltipText = () => {
    const { connectionStatus, latency, isOnline, downloadSpeed } = connectionState;

    if (connectionStatus === CONNECTION_STATUS.CHECKING) {
      return 'Checking connection...';
    }

    if (!isOnline || connectionStatus === CONNECTION_STATUS.OFFLINE) {
      return 'No internet connection';
    }

    let signalText = 'Internet connected';
    if (latency !== null) {
      if (latency < 100) signalText = `Excellent signal (${latency}ms)`;
      else if (latency < 300) signalText = `Good signal (${latency}ms)`;
      else if (latency < 500) signalText = `Fair signal (${latency}ms)`;
      else if (latency < 1000) signalText = `Weak signal (${latency}ms)`;
      else signalText = `Very weak signal (${latency}ms)`;
    }

    // เพิ่มข้อมูลความเร็วดาวน์โหลด
    if (downloadSpeed !== null) {
      return `${signalText} • ${downloadSpeed} KB/s`;
    }

    return signalText;
  };

  const renderIcon = () => {
    const { connectionStatus, strength, downloadSpeed, speedLevel } = connectionState;

    const props = {
      status: connectionStatus,
      strength: strength || SIGNAL_STRENGTH.EXCELLENT,
      size,
      activeColor,
      inactiveColor,
      downloadSpeed,
      speedLevel
    };

    switch (type) {
      case 'curved':
        return <WifiIcon {...props} />;
      case 'gradient':
        return <GradientWifiIcon {...props} />;
      case 'simple':
      default:
        return <SimpleWifiIcon {...props} />;
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: 'inline-flex',
        position: 'relative',
        cursor: 'default'
      }}
      onMouseEnter={() => showTooltip && setShowTooltipState(true)}
      onMouseLeave={() => showTooltip && setShowTooltipState(false)}
      title={showTooltip ? getTooltipText() : undefined}>
      {renderIcon()}
      <div className='text-white text-[11px] flex items-center ml-1 leading-5'>
        <span>
          {connectionState.latency !== null ? `${connectionState.latency}ms` : '-'} / {connectionState.downloadSpeed !== null ? `${connectionState.downloadSpeed}KB/s` : '-'}
        </span>
      </div>
      {/* Tooltip */}
      {/* {showTooltip && showTooltipState && (
        <div
          ref={tooltipRef}
          style={{
            position: 'absolute',
            bottom: '100%',
            ...tooltipPosition,
            marginBottom: '8px',
            padding: '6px 12px',
            backgroundColor: '#1f2937',
            color: '#f3f4f6',
            fontSize: '12px',
            borderRadius: '6px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 9999,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            animation: 'fadeIn 0.2s ease-in'
          }}>
          {getTooltipText()}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: tooltipPosition.left === 'auto' ? 'auto' : tooltipPosition.left === '0' ? '10px' : '50%',
              right: tooltipPosition.right === '0' ? '10px' : 'auto',
              transform: tooltipPosition.left === '50%' ? 'translateX(-50%)' : 'none',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid #1f2937'
            }}
          />
        </div>
      )} */}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default WifiSignal;
